import {
  Box,
  Chip,
  CircularProgress,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { DataGridPro } from "@mui/x-data-grid-pro";
import { useMemo, useState } from "react";
import SearchIcon from "@mui/icons-material/Search";
import RefreshIcon from "@mui/icons-material/Refresh";
import IntelligenceTimeFilter from "../../../components/IntelligenceTimeFilter";
import useLecsEventCounts from "./useLecsEventCounts";
import { dataGridStyle, palette } from "../../../theme";
import API from "../../../api";
import {
  startToast,
  updateToastError,
  updateToastSuccess,
} from "../../../utils/notification-loading";
import { MUTED } from "./resellerUi";

const STATUS_COLOR = {
  online: "#34a327",
  offline: palette.error.light,
  unauthorized: "rgba(234,234,234,0.35)",
};

const statusOf = (x) =>
  x?.online === true ? "online" : x?.online === false ? "offline" : "unauthorized";

const STATUS_FILTERS = [
  { key: "all", label: "All" },
  { key: "online", label: "Online" },
  { key: "offline", label: "Offline" },
  { key: "unauthorized", label: "Unauthorized" },
  { key: "events", label: "With events" },
];

const CRITICAL_COLOR = "#E24B4A";
const HIGH_COLOR = "#eb7434";
const hasEvents = (x) => (x?.critical ?? 0) > 0 || (x?.high ?? 0) > 0;

const LecsTable = ({ lecs, setTimeZone, setLecsInfo, setCustomerName, dateFrom, dateTo, dateFilter, onDateChange }) => {
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");

  const { counts: eventCounts, isLoading, error, refresh, goToEvents } = useLecsEventCounts(dateFrom, dateTo);

  const rows = useMemo(
    () =>
      (Array.isArray(lecs) ? lecs : []).map((x) => ({
        ...x,
        critical: eventCounts.get(x?.matricola)?.critical ?? 0,
        high: eventCounts.get(x?.matricola)?.high ?? 0,
      })),
    [lecs, eventCounts]
  );

  const counts = useMemo(() => {
    const c = { all: rows.length, online: 0, offline: 0, unauthorized: 0, events: 0 };
    rows.forEach((x) => {
      c[statusOf(x)] += 1;
      if (hasEvents(x)) c.events += 1;
    });
    return c;
  }, [rows]);

  const filteredLecs = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((x) => {
      if (status === "events" ? !hasEvents(x) : status !== "all" && statusOf(x) !== status) return false;
      if (!q) return true;
      return [x?.matricola, x?.nomeLecs, x?.email, x?.telefono, x?.ragioneSociale]
        .some((v) => String(v ?? "").toLowerCase().includes(q));
    });
  }, [rows, search, status]);

  function handleRedirect(matricola, ragioneSociale) {
    startToast();

    API.postCurrentCustomer({ serial_number: matricola })
      .then((res) => {
        const company = ragioneSociale ? ragioneSociale : res.data.currentCustomer;
        setCustomerName(company);

        API.getTimeZone()
          .then((res) => setTimeZone(res.data))
          .catch((err) => {});

        API.getLecsStatus()
          .then((res) => setLecsInfo(res.data))
          .catch((err) => {});

        updateToastSuccess("Customer selected successfully");
        sessionStorage.setItem("selectedTab", "Customer Management");
        window.location.href = "/reseller/myC/dashboard";
      })
      .catch((err) => {
        updateToastError("Not authorized to view this customer");
        
      });
  }

  const searchRow = (
    <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
      <TextField
        size="small"
        placeholder="Search LECS, name, company…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        sx={{ minWidth: 240, flex: 1, maxWidth: 360 }}
        slotProps={{
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" sx={{ color: MUTED }} />
              </InputAdornment>
            ),
          },
        }}
      />
      <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
        {STATUS_FILTERS.map(({ key, label }) => {
          const active = status === key;
          return (
            <Chip
              key={key}
              size="small"
              clickable
              onClick={() => setStatus(key)}
              label={
                <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.75 }}>
                  {key !== "all" && key !== "events" && <StatusDot status={key} />}
                  {`${label} · ${counts[key]}`}
                </Box>
              }
                            sx={{
                color: active ? "#eaeaea" : MUTED,
                backgroundColor: active ? "rgba(69,144,149,0.18)" : "transparent",
                border: "1px solid",
                borderColor: active ? palette.primary.main : "rgba(255,255,255,0.14)",
                "&:hover": { borderColor: palette.primary.light, backgroundColor: "rgba(69,144,149,0.12)" },
              }}
            />
          );
        })}
      </Stack>
    </Stack>
  );

  const actions = (
    <>
      {error && <Typography sx={{ fontSize: 12, color: palette.error.light }}>{error}</Typography>}
      {isLoading && <CircularProgress size={16} />}
      <Tooltip title="Refresh events">
        <span>
          <IconButton size="small" onClick={refresh} disabled={isLoading} aria-label="Refresh events">
            <RefreshIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
    </>
  );

  const toolbar = (
    <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
      {searchRow}
      <Box sx={{ ml: "auto", "& > .MuiBox-root": { mb: 0 } }}>
        <IntelligenceTimeFilter
          compact
          disabledPresets={["30D"]}
          onDateChange={onDateChange}
          initialDateFrom={dateFilter?.dateFrom}
          initialDateTo={dateFilter?.dateTo}
        />
      </Box>
      {actions}
    </Stack>
  );

  const columns = useMemo(() => getColumns(goToEvents), [goToEvents]);

  return (
    <>
      <Box mb={2}>{toolbar}</Box>
      <Box sx={{ height: "calc(100vh - 320px)", minHeight: 460 }}>
        <DataGridPro
          sx={{
            ...dataGridStyle,
            "& .MuiDataGrid-row": { cursor: "pointer" },
            "& .MuiDataGrid-columnHeader .MuiBadge-badge": { display: "none" },
          }}
          rows={filteredLecs}
          columns={columns}
          initialState={{
            sorting: { sortModel: [{ field: "critical", sort: "desc" }, { field: "high", sort: "desc" }] },
          }}
          disableRowSelectionOnClick
          localeText={{ noRowsLabel: "No LECS found" }}
          onRowClick={(params) =>
            handleRedirect(params.row.matricola, params.row.ragioneSociale)
          }
        />
      </Box>
    </>
  );
};

export default LecsTable;

const StatusDot = ({ status }) => (
  <Box
    component="span"
    sx={{ width: 8, height: 8, borderRadius: "50%", display: "inline-block", backgroundColor: STATUS_COLOR[status] }}
  />
);

const Muted = ({ children }) => (
  <Typography component="span" sx={{ fontSize: 13, color: MUTED }}>{children}</Typography>
);

const getColumns = (goToEvents) => [
  {
    field: "matricola",
    headerName: "LECS",
    width: 110,
  },
  {
    field: "nomeLecs",
    headerName: "Name",
    width: 150,
    renderCell: (params) => params.value || <Muted>No name</Muted>,
  },
  {
    field: "online",
    headerName: "Status",
    width: 130,
    valueGetter: (value) => statusOf({ online: value }),
    renderCell: (params) => (
      <Stack direction="row" spacing={1} alignItems="center">
        <StatusDot status={params.value} />
        <Typography sx={{ fontSize: 13, textTransform: "capitalize" }}>{params.value}</Typography>
      </Stack>
    ),
  },
  {
    field: "ragioneSociale",
    headerName: "Company",
    width: 250,
    flex: 1,
    renderCell: (params) => params.value || <Muted>No user found</Muted>,
  },
  {
    field: "email",
    headerName: "Email",
    width: 240,
    renderCell: (params) => params.value || <Muted>—</Muted>,
  },
  {
    field: "daysLeft",
    headerName: "Days left",
    width: 110,
    renderCell: (params) => {
      const v = params.value;
      if (v === null || v === undefined || v === "") return <Muted>—</Muted>;
      const n = Number(v);
      const color = !Number.isFinite(n) ? undefined : n <= 0 ? palette.error.light : n <= 30 ? "#EF9F27" : undefined;
      return <Typography component="span" sx={{ fontSize: 13, color }}>{v}</Typography>;
    },
  },
  eventColumn("critical", "Critical", CRITICAL_COLOR, goToEvents),
  eventColumn("high", "High", HIGH_COLOR, goToEvents),
];

function eventColumn(field, headerName, color, goToEvents) {
  return {
    field,
    headerName,
    type: "number",
    width: 110,
    align: "left",
    headerAlign: "left",
    renderCell: (params) => {
      const n = params.value ?? 0;
      if (n <= 0) return <Muted>—</Muted>;
      return (
        <Tooltip title={`Open ${headerName.toLowerCase()} logs`}>
          <Box
            component="button"
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              goToEvents(field, params.row.matricola);
            }}
            sx={{
              cursor: "pointer",
              fontFamily: "inherit",
              px: 0.8,
              py: 0.15,
              borderRadius: 0.5,
              color,
              bgcolor: `${color}22`,
              border: `1px solid ${color}`,
              fontSize: 12,
              fontWeight: 700,
              "&:hover": { bgcolor: `${color}44` },
            }}
          >
            {n}
          </Box>
        </Tooltip>
      );
    },
  };
}
